import React from "react";
import { CheckCircle, Loader2 } from "lucide-react";
import type {
  ComponentResponse,
  SspComponentResponse,
} from "../../../../../api/frameworksApi";
import { Modal, ModalFooterButtons } from "../../../../components/Modal";

import type { CdefComponent } from "../../types";
export type { CdefComponent };

export interface ImportComponentFromLibraryDialogProps {
  open: boolean;
  onClose: () => void;
  handleImportComponents: (sourceComponentIds: string[]) => void;
  importingComponents: boolean;
  selectedImportComponentIds: Set<string>;
  setSelectedImportComponentIds: React.Dispatch<
    React.SetStateAction<Set<string>>
  >;
  sspComponents: SspComponentResponse[];
  cdefComponents: CdefComponent[];
  cdefLoading: boolean;
  importedComponentIds: Set<string>;
}

export function ImportComponentFromLibraryDialog({
  open,
  onClose,
  handleImportComponents,
  importingComponents,
  selectedImportComponentIds,
  setSelectedImportComponentIds,
  sspComponents,
  cdefComponents,
  cdefLoading,
  importedComponentIds,
}: ImportComponentFromLibraryDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Import from Component Library"
      description="Select components to import into this SSP"
      size="md"
      footer={
        <ModalFooterButtons
          onCancel={onClose}
          onSubmit={() =>
            handleImportComponents([...selectedImportComponentIds])
          }
          submitLabel={
            importingComponents
              ? "Importing"
              : `Import Selected (${selectedImportComponentIds.size})`
          }
          submitDisabled={
            importingComponents || selectedImportComponentIds.size === 0
          }
        />
      }
    >
      {(() => {
        // Build set of source component IDs already in this SSP
        const alreadyImportedSourceIds = new Set(
          sspComponents
            .map((c) => c.sourceComponentId)
            .filter(Boolean) as string[],
        );
        // Filter out components already imported into this SSP
        const availableComponents = cdefComponents.filter(
          (comp) =>
            !alreadyImportedSourceIds.has(comp.id) &&
            !importedComponentIds.has(comp.id),
        );

        if (cdefLoading) {
          return (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="size-5 text-indigo-600 animate-spin" />
              <span className="ml-3 text-sm text-muted-foreground">
                Loading components
              </span>
            </div>
          );
        }

        if (cdefComponents.length === 0) {
          return (
            <p className="text-xs text-muted-foreground text-center py-6">
              No components found in the library.
            </p>
          );
        }

        if (availableComponents.length === 0) {
          return (
            <div className="flex flex-col items-center py-8 gap-2">
              <CheckCircle className="size-6 text-emerald-500" />
              <p className="text-sm font-medium text-muted-foreground">
                All components have been imported
              </p>
              <p className="text-xs text-muted-foreground">
                {cdefComponents.length} component
                {cdefComponents.length !== 1 ? "s" : ""} already in this SSP
              </p>
            </div>
          );
        }

        return (
          <div className="space-y-1.5">
            {availableComponents.length < cdefComponents.length && (
              <p className="text-xs text-muted-foreground mb-2">
                Showing {availableComponents.length} of{" "}
                {cdefComponents.length} components
                <span className="text-emerald-600 dark:text-emerald-400 ml-1">
                  ({cdefComponents.length - availableComponents.length}{" "}
                  already imported)
                </span>
              </p>
            )}
            <div className="flex items-center justify-between px-1 pb-1">
              <span className="text-xs text-muted-foreground">
                {selectedImportComponentIds.size} selected
              </span>
              <button
                type="button"
                onClick={() =>
                  setSelectedImportComponentIds(
                    new Set(
                      selectedImportComponentIds.size ===
                        availableComponents.length
                        ? []
                        : availableComponents.map((comp) => comp.id),
                    ),
                  )
                }
                className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                {selectedImportComponentIds.size ===
                  availableComponents.length
                  ? "Clear all"
                  : "Select all"}
              </button>
            </div>
            {availableComponents.map((comp) => (
              <div
                key={comp.id}
                className="flex items-center gap-2 p-2 rounded-lg border border-border"
              >
                <input
                  type="checkbox"
                  checked={selectedImportComponentIds.has(comp.id)}
                  onChange={() =>
                    setSelectedImportComponentIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(comp.id)) next.delete(comp.id);
                      else next.add(comp.id);
                      return next;
                    })
                  }
                  className="size-4 rounded border-border accent-indigo-600 flex-shrink-0 cursor-pointer"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{comp.title}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-[10px] font-bold px-1 py-0.5 rounded bg-muted text-muted-foreground capitalize">
                      {comp.componentType}
                    </span>
                    <span className="text-[10px] px-1 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 truncate">
                      {comp.cdefTitle}
                    </span>
                  </div>
                </div>
                {selectedImportComponentIds.has(comp.id) && (
                  <span className="flex items-center gap-1 px-2 py-1 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 text-[10px] font-bold text-indigo-600 dark:text-indigo-400 flex-shrink-0">
                    <CheckCircle className="size-3" />
                    Selected
                  </span>
                )}
              </div>
            ))}
          </div>
        );
      })()}
    </Modal>
  );
}
